import type { IconType } from "antd/es/notification"
import humps from "humps"

import type { Customization } from "@/api/customization"
import type { Environment } from "@/api/environment"
import type { Org } from "@/api/orgs"
import type { DenormalizedTemplate } from "@/api/templates"
import type { User } from "@/api/users"
import type { ImportWebhook } from "@/api/webhooks"
import type { DenormalizedWorkspace } from "@/api/workspaces"
import type { CanCanAbility } from "@/util/abilities"
import { stringToDayjs } from "@/util/dates"
import type { Feature, PermanentFeature } from "@/util/feature"
import type { LlmFeatureCategory } from "@/util/llmConfig"
import { hideInternalFeatures } from "@/util/showInternalFeatures"

export type AiFeatureEnablement = Partial<
  Record<LlmFeatureCategory, { enabled: boolean }>
>

interface Flash {
  type: IconType
  text: string
}

export interface ViewData {
  user?: User
  flash?: Flash
  // Catch-all for keys not listed explicitly below. These are values sent
  // down from the backend, so they are untyped until narrowed by the
  // corresponding `get(key: "…")` overload.
  [key: string]: unknown
  org?: Org
  features: { [feature in Feature | PermanentFeature]: boolean }
  // Values for numeric Harness flags. Entries are nil when the Split
  // treatment is missing/non-numeric/non-positive; consume via
  // `getFeatureNumber` in `frontend/src/util/gating.ts`.
  featureNumbers: Partial<Record<Feature | PermanentFeature, number | null>>
  // Stores the other Orgs that the login user has access to.
  otherOrgs?: Org[]

  // Only used on /select-organization page to store the possible Orgs
  // the user can access.
  orgs?: Org[]

  // only used on init session through UI
  sessionToken?: string

  root_host: string
  // Chain-wide trace id (`trx_`) for this page render. Surfaced to internal
  // users via the FrameHeader trx footer for log/Sentry correlation.
  transactionId?: string
  preview?: string
  previewStatus?: "ready" | "failed" | "landed" | "abandoned"
  previewBranch?: string
  previewCommit?: string
}

export type ViewDataFromServer = {
  // Values may be of a different casing (snake_case from the Rails backend)
  // before `humps.camelizeKeys` is applied in `updateWithData`.
  [key: keyof ViewData]: unknown
}

/**
 * This component is the only interface through which back-end variables passed down
 * as locals should be accessed. As globals, they should be read-only and this
 * component enforces them as such.
 */
class ViewManager {
  private viewData: ViewData

  constructor(data: ViewDataFromServer) {
    this.viewData = this.updateWithData(data)
  }

  // This should only be called externally by the EmbeddedLauncher to
  // reset the viewManager's internal state after initializing the
  // embedded session.
  updateWithData(data: ViewDataFromServer) {
    // Don't camelize the keys of data.features or data.feature_numbers
    // — they are the Split flag names (PascalCase) and must be preserved.
    const { features, feature_numbers, ai_feature_enablement, ...restData } = data
    this.viewData = humps.camelizeKeys(restData) as ViewData
    this.viewData.features = features as ViewData["features"]
    this.viewData.featureNumbers =
      (feature_numbers as ViewData["featureNumbers"] | undefined) ?? {}
    this.viewData.aiFeatureEnablement = ai_feature_enablement

    if (this.viewData.org) {
      const origOrg = this.viewData.org as Omit<Org, "trialEnd"> & {
        trialEnd: string | null
      }
      this.viewData.org.trialEnd =
        origOrg.trialEnd != null ? stringToDayjs(origOrg.trialEnd) : null
    }

    return this.viewData
  }

  // Specify return types for specific keys.
  get(key: "org"): Org | undefined
  get(key: "user"): User | undefined
  get(key: "orgs"): Org[] | undefined
  get(key: "otherOrgs"): Org[] | undefined
  get(key: "features"): { [index in Feature | PermanentFeature]: boolean }
  get(key: "abilities"): CanCanAbility[]
  get(key: "customization"): Customization
  get(key: "sessionToken"): string | undefined
  get(key: "isInternalAdmin"): boolean
  get(key: "stytchSsoConnectionId"): string | undefined
  get(key: "ssoEnforced"): boolean | undefined
  get(key: "flash"): Flash | undefined
  get(key: "supportChatProvider"): "pylon" | undefined
  get(key: "previewStatus"): "ready" | "failed" | "landed" | "abandoned" | undefined
  get(key: "previewBranch"): string | undefined
  get(key: "previewCommit"): string | undefined
  get(key: "transactionId"): string | undefined
  get(key: "webhooks"): ImportWebhook[] | undefined
  get(key: "featureNumbers"): Partial<Record<Feature | PermanentFeature, number | null>>
  get(key: "aiFeatureEnablement"): AiFeatureEnablement | undefined
  get(key: "importerAcceptedFileExtensionsOverride"): string[] | undefined
  get(key: "pdfUploadEnabled"): boolean
  // Authorization URIs are provided by the backend on the login page view.
  // They are expected to be present whenever the corresponding sign-in button
  // is rendered; typed non-optional to match existing call-site assumptions.
  get(key: "googleAuthorizationUri"): string
  get(key: "microsoftAuthorizationUri"): string
  // Catch-all for unspecified keys. Prefer adding a typed overload above
  // over consuming `unknown` at the call site.
  get(key: string): unknown

  get(key: string) {
    if (!this.viewData) {
      return undefined
    }

    return this.viewData[key]
  }

  getOnceThenReadFromStore(key: "templates"): DenormalizedTemplate[]
  getOnceThenReadFromStore(key: "workspaces"): DenormalizedWorkspace[]
  getOnceThenReadFromStore(key: "environments"): Environment[]
  getOnceThenReadFromStore(key: string): unknown

  getOnceThenReadFromStore(key: string): unknown {
    return this.get(key)
  }

  updateUser(user: User) {
    this.viewData.user = user
  }

  getViewData(): ViewData {
    return this.viewData
  }
}

// ROOT_HOST looks like:
// - oneschema.co
// - eu.oneschema.co
// - oneschema.example.com
// - us.oneschema.example.com
export const ROOT_HOST = (window.viewData as ViewData).root_host

export function getEmbedUrlFromRootHost() {
  return `${window.location.protocol}//embed.${ROOT_HOST}`
}

export const viewManager = new ViewManager(window.viewData as ViewDataFromServer)

export function isInternalAdmin(): boolean {
  return viewManager.get("isInternalAdmin")
}

export function showInternalTools(): boolean {
  return isInternalAdmin() && !hideInternalFeatures()
}
